// guidePrimitives — shared building blocks for the in-portal User Guide.
//
// The guide is prose, not a data surface, so it needs a handful of primitives the
// rest of the portal doesn't have (callouts, a prose-scale accordion, a doc table).
// Everything here is presentational and dependency-free apart from Badge, which is
// deliberately reused so a "Critical" chip in the guide is pixel-identical to the
// one on the Alerts page.
//
// Deliberately NOT reusing sourceEvents/CollapsibleSection: its header is a tiny
// uppercase label tuned for dense alert panels and reads badly at prose scale.
import React, { useState } from 'react';
import {
  ChevronRightIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

// ── Callout ────────────────────────────────────────────────────────────────
// info = context worth knowing · warn = get this wrong and it costs you · tip = shortcut
const CALLOUT_STYLES = {
  info: {
    wrap: 'bg-blue-950/30 border-blue-700/40',
    icon: 'text-blue-400',
    Icon: InformationCircleIcon,
  },
  warn: {
    wrap: 'bg-amber-950/30 border-amber-700/40',
    icon: 'text-amber-400',
    Icon: ExclamationTriangleIcon,
  },
  tip: {
    wrap: 'bg-emerald-950/25 border-emerald-700/40',
    icon: 'text-emerald-400',
    Icon: LightBulbIcon,
  },
};

export function Callout({ tone = 'info', children }) {
  const s = CALLOUT_STYLES[tone] || CALLOUT_STYLES.info;
  const Icon = s.Icon;
  return (
    <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 my-4 text-sm text-gray-300 ${s.wrap}`}>
      <Icon className={`h-5 w-5 flex-none mt-0.5 ${s.icon}`} />
      <div className="min-w-0 space-y-2 leading-relaxed">{children}</div>
    </div>
  );
}

// ── Accordion block (replaces the guide's <details> elements) ──────────────
export function GuideDetails({ summary, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card p-0 overflow-hidden mb-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-gray-800/30 transition-colors"
      >
        <ChevronRightIcon
          className={`h-4 w-4 flex-none text-gray-600 transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <span className="text-sm font-semibold text-gray-200">{summary}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 pl-10 text-sm text-gray-400 leading-relaxed space-y-3 border-t border-gray-800/60">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Prose card (the guide's .card blocks) ──────────────────────────────────
export function GuideCard({ title, badge, children }) {
  return (
    <div className="card mb-4">
      {title && (
        <h4 className="text-sm font-semibold text-gray-200 mb-2 flex items-center gap-2 flex-wrap">
          {title}
          {badge}
        </h4>
      )}
      <div className="text-sm text-gray-400 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

// ── Doc table ──────────────────────────────────────────────────────────────
// cols: array of header strings. rows: array of arrays of ReactNode.
export function GuideTable({ cols, rows }) {
  return (
    <div className="card p-0 overflow-x-auto my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-900/60 border-b border-gray-800">
            {cols.map((c, i) => (
              <th
                key={i}
                className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-800/60 last:border-0 align-top">
              {r.map((cell, j) => (
                <td key={j} className={`px-3 py-2 leading-relaxed ${j === 0 ? 'text-gray-300' : 'text-gray-400'}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Inline text helpers ────────────────────────────────────────────────────
export function K({ children }) {
  return (
    <code className="px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-[12px] font-mono text-gray-300">
      {children}
    </code>
  );
}

export function B({ children }) {
  return <strong className="text-gray-200 font-semibold">{children}</strong>;
}

// In-page cross-reference. Smooth-scrolls to another section of the same guide.
export function Xref({ to, children }) {
  const jump = (e) => {
    e.preventDefault();
    const el = document.getElementById(to);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <a href={`#${to}`} onClick={jump} className="text-blue-400 hover:text-blue-300 hover:underline">
      {children}
    </a>
  );
}

// A colored bell swatch for the inline-suppress table.
export function BellSwatch({ className }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 flex-none">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.143 17.082a24 24 0 0 0 3.844.148m-3.844-.148a23.9 23.9 0 0 1-4.756-.98 0.75.75 0 0 1-.298-1.205A8.25 8.25 0 0 0 6 9.75V9m3.143 8.082A24 24 0 0 0 12.987 17.23M15 19.5a3 3 0 1 1-6 0M3 3l18 18" />
      </svg>
    </span>
  );
}

// ── Section heading ────────────────────────────────────────────────────────
// Renders an anchor-linkable heading. GuideLayout builds its table of contents
// by querying `h2[id], h3[id]` from the rendered DOM, so pages never hand-maintain
// a TOC list that can drift from the content.
export function Section({ id, title, level = 2, children }) {
  const Tag = level === 2 ? 'h2' : 'h3';
  const cls =
    level === 2
      ? 'text-lg font-bold text-gray-100 mt-10 mb-3 pb-2 border-b border-gray-800 scroll-mt-6'
      : 'text-base font-semibold text-gray-200 mt-7 mb-2 scroll-mt-6';

  return (
    <section>
      <Tag id={id} className={cls}>
        {title}
      </Tag>
      {children}
    </section>
  );
}

// Sub-heading inside a section (the guide's h4s). Not indexed in the TOC.
export function H4({ children }) {
  return <h4 className="text-sm font-semibold text-gray-300 mt-5 mb-2">{children}</h4>;
}

// Plain prose paragraph + list wrappers, so pages read as content not classnames.
export function P({ children }) {
  return <p className="text-sm text-gray-400 leading-relaxed mb-3">{children}</p>;
}

export function UL({ children }) {
  return <ul className="text-sm text-gray-400 leading-relaxed mb-3 space-y-1.5 list-disc pl-5 marker:text-gray-600">{children}</ul>;
}

export function OL({ children }) {
  return <ol className="text-sm text-gray-400 leading-relaxed mb-3 space-y-1.5 list-decimal pl-5 marker:text-gray-600">{children}</ol>;
}
